/**
 * 계약 청구 수신 정보 컴포넌트
 * 계약서의 청구 수신 정보를 표시하고 관리하는 컴포넌트입니다.
 * 청구서 수신 이메일, 담당자 정보 등을 표시하고 편집할 수 있습니다.
 */

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { BillingDetail, ScheduleNoti } from "src/api/billingSchedule/billing-type";
import { patchScheduleDetail_RC002 } from "src/api/billingSchedule/billingSchedule-api";
import { ContractorNoti, ContractStep, DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useModal } from "src/recoil/modalState/hook";
import ContractNotificationForm from "./editComponent/ContractNotificationForm";

interface NotificationRow {
  label: string;
  email?: ScheduleNoti;
  kakao?: ScheduleNoti;
}

interface NotificationFormData {
  notificationRows: NotificationRow[];
  billContactList: ContractorNoti[];
}

const NoEditStateNoS2: Array<string> = [ContractStep.APPLY_CANCEL, ContractStep.TERMINATE_RECEIVED, ContractStep.TERMINATE_COMPLETE];
const NoEditStateS2: Array<string> = NoEditStateNoS2.concat([
  ContractStep.CONTRACT_ACCEPT,
  ContractStep.USE_APPROVAL,
  ContractStep.USE_PROGRESS,
  ContractStep.USE_COMPLETE,
]);
// 공간임대(FULL_COURT)는 해지접수/해지완료 상태에서도 편집 가능 (신청취소만 비활성)
const NoEditStateFullCourt: Array<string> = [ContractStep.APPLY_CANCEL];

type Props = {
  detailData: DetailTotalData; // 계약 상세 정보
  setData: Function; // 데이터 업데이트 함수
};

/**
 * 계약 청구 수신 정보 컴포넌트
 * @param detailData 계약 상세 정보
 * @param setData 데이터 업데이트 함수
 */
const ContractBillingScheduleNoti = ({ detailData, setData }: Props) => {
  const { setBaseModal } = useModal();

  const [editable, setEditable] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm<NotificationFormData>({
    defaultValues: {
      notificationRows: [],
      billContactList: [],
    },
  });

  const disabledState = useMemo(
    () => [String(detailData?.contract?.contractStep), String(detailData?.product?.productType)],
    [detailData?.contract?.contractStep, detailData?.product?.productType],
  );

  // 편집 버튼 비활성화 여부 (공간임대는 해지접수/해지완료에도 편집 가능)
  const isEditButtonDisabled = useMemo(() => {
    const [step, productType] = disabledState;
    if (productType === "TIME_COURT") {
      return !NoEditStateS2.includes(step);
    }
    const noEditList = productType === "FULL_COURT" ? NoEditStateFullCourt : NoEditStateNoS2;
    return noEditList.includes(step);
  }, [disabledState]);

  const { isAuthority } = usePartnerAuthority();

  const { executeAsync: editSchedule } = useApiOperation(patchScheduleDetail_RC002);

  // 초기 데이터 세팅
  useEffect(() => {
    const scheduleMessageList = detailData?.scheduleList?.find((schedule: BillingDetail) => schedule.supplyType === "RENTAL")?.scheduleMessageList;
    const billContactList = detailData?.contract?.billContactList?.filter((item) => item.receiveNotiType === "TALK_OFFICIALDOC");

    if (!scheduleMessageList?.length) return;

    // messageType별로 그룹핑
    const groupedMap = scheduleMessageList.reduce((result, item) => {
      const messageType = item.messageType || "";

      if (!result[messageType]) {
        result[messageType] = { label: item.messageLabel || "" };
      }

      if (item.messageMedia === "EMAIL") {
        result[messageType].email = item;
      } else if (item.messageMedia === "KAKAOTALK") {
        result[messageType].kakao = item;
      }

      return result;
    }, {} as Record<string, NotificationRow>);

    reset({
      notificationRows: Object.values(groupedMap),
      billContactList: billContactList || [],
    });
  }, [detailData?.scheduleList, detailData?.contract?.billContactList, reset]);

  // 체크박스 변경 핸들러
  const handleNotificationToggle = (messageId: string | number | undefined, checked: boolean) => {
    if (!messageId) return;

    const currentRows = watch("notificationRows");
    const updatedRows = currentRows.map((row) => {
      if (row.email?.messageId === messageId) {
        return { ...row, email: { ...row.email, isSend: checked } };
      }
      if (row.kakao?.messageId === messageId) {
        return { ...row, kakao: { ...row.kakao, isSend: checked } };
      }
      return row;
    });

    setValue("notificationRows", updatedRows);
  };

  // 이메일 추가
  const handleAddEmail = () => {
    const currentList = watch("billContactList");
    const activeEmails = currentList.filter((item) => item.cmdType !== "D");

    if (activeEmails.length >= 5) {
      return;
    }

    setValue("billContactList", [
      ...currentList,
      {
        email: "",
        receiveNotiType: "TALK_OFFICIALDOC",
        cmdType: "C",
        isDeleted: false,
      },
    ]);
  };

  // 이메일 삭제 (cmdType: "D"로 변경)
  const handleRemoveEmail = (index: number) => {
    const currentList = watch("billContactList");
    const updatedList = currentList.map((item, idx) => {
      if (idx === index) {
        return { ...item, cmdType: "D" };
      }
      return item;
    });

    setValue("billContactList", updatedList);
  };

  const onConfirmBillingsData = handleSubmit(async (formData) => {
    setBaseModal({
      isOpen: true,
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
      title: "안내문 설정",
      children: "저장하시겠습니까?",
      onClick: () => {
        onSaveBillingsData(formData);
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  });

  // 저장
  const onSaveBillingsData = async (formData: NotificationFormData) => {
    // NotificationRow를 원본 ScheduleNoti[] 형태로 변환
    const scheduleMessageList: ScheduleNoti[] = [];

    const billContactList = formData.billContactList.map((item) => {
      return {
        ...item,
        cmdType: item.cmdType ? item.cmdType : item.billContactId ? "U" : "C",
      };
    });

    formData.notificationRows.forEach((row) => {
      if (row.email) {
        scheduleMessageList.push(row.email);
      }
      if (row.kakao) {
        scheduleMessageList.push(row.kakao);
      }
    });
    const scheduleId = detailData?.scheduleList?.find((schedule: BillingDetail) => schedule.supplyType === "RENTAL")?.scheduleId;
    try {
      await editSchedule({
        contractId: Number(detailData?.contract?.contractId),
        billContactList: billContactList,
        scheduleList: [
          {
            scheduleId: scheduleId,
            scheduleMessageList: scheduleMessageList.map((item) => {
              return {
                ...item,

                cmdType: item.messageId ? "U" : "C",
              };
            }),
          },
        ],
      });

      setEditable(false);
      setData();
    } catch (error) {
      console.error("저장 실패:", error);
    }
  };

  return (
    <article className={`contents-container__wrap-article full-width `}>
      {isAuthority("w") && (
        <div className="contents-container__edit-area">
          {editable ? (
            <div className="flex-center">
              <BaseButton
                type="button"
                className="size-small color-white mr10"
                title={"취소"}
                onClick={() => {
                  setEditable(false);
                  // 폼 리셋
                  const scheduleMessageList = detailData?.scheduleList?.find(
                    (schedule: BillingDetail) => schedule.supplyType === "RENTAL",
                  )?.scheduleMessageList;
                  const billContactList = detailData?.contract?.billContactList?.filter((item) => item.receiveNotiType === "TALK_OFFICIALDOC");

                  if (scheduleMessageList) {
                    const groupedMap = scheduleMessageList.reduce((result, item) => {
                      const messageType = item.messageType || "";
                      if (!result[messageType]) {
                        result[messageType] = { label: item.messageLabel || "" };
                      }
                      if (item.messageMedia === "EMAIL") {
                        result[messageType].email = item;
                      } else if (item.messageMedia === "KAKAOTALK") {
                        result[messageType].kakao = item;
                      }
                      return result;
                    }, {} as Record<string, NotificationRow>);

                    reset({
                      notificationRows: Object.values(groupedMap),
                      billContactList: billContactList || [],
                    });
                  }
                }}
              />
              <BaseButton type="button" className="size-small" title={"저장"} onClick={onConfirmBillingsData} />
            </div>
          ) : (
            <button className="base-edit-btn" disabled={isEditButtonDisabled} onClick={() => setEditable(true)} />
          )}
        </div>
      )}
      <BaseSectionTitle title={"안내문 설정"} />
      <section className="contents-container__1160">
        <ContractNotificationForm
          control={control}
          editable={editable}
          notificationRows={watch("notificationRows")}
          billContactList={watch("billContactList")}
          detailData={detailData}
          onNotificationToggle={handleNotificationToggle}
          onAddEmail={handleAddEmail}
          onRemoveEmail={handleRemoveEmail}
        />
      </section>
    </article>
  );
};
export default ContractBillingScheduleNoti;
